import { useState, useEffect } from 'react'
import { apiFetch } from '../services/auth'
import { ProjectOverlay } from './ProjectOverlay'

interface FortnoxProject {
  ProjectNumber: string
  Description: string
  Status: 'ONGOING' | 'COMPLETED'
  StartDate?: string
  EndDate?: string
  ProjectLeader?: string
  ContactPerson?: string
  Comments?: string
}

interface Project {
  id: string
  name: string
  description?: string
  status: 'active' | 'completed' | 'on-hold'
  startDate?: string
  endDate?: string
  projectLeader?: string
  contactPerson?: string
  comments?: string
}

export function Projects() {
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAddOverlay, setShowAddOverlay] = useState(false)
  const [editingProject, setEditingProject] = useState<Project | null>(null)
  const [deletingProject, setDeletingProject] = useState<Project | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const mapFortnoxProject = (fortnoxProject: FortnoxProject): Project => {
    let status: Project['status'] = 'active'
    
    if (fortnoxProject.Status === 'COMPLETED') {
      status = 'completed'
    } else if (fortnoxProject.Status === 'ONGOING') {
      status = 'active'
    }

    return {
      id: fortnoxProject.ProjectNumber,
      name: fortnoxProject.Description || `Projekt ${fortnoxProject.ProjectNumber}`,
      description: fortnoxProject.Comments,
      status,
      startDate: fortnoxProject.StartDate,
      endDate: fortnoxProject.EndDate,
      projectLeader: fortnoxProject.ProjectLeader,
      contactPerson: fortnoxProject.ContactPerson,
      comments: fortnoxProject.Comments
    }
  }

  const fetchProjects = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiFetch('/fortnox/projects')

      if (!response.ok) {
        if (response.status === 400) {
          const errorData = await response.json()
          if (errorData.error === 'Fortnox not connected') {
            throw new Error('Fortnox är inte ansluten. Gå till Inställningar för att ansluta.')
          }
        }
        throw new Error(`Failed to fetch projects: ${response.statusText}`)
      }

      const data = await response.json()
      const fortnoxProjects = data.Projects || []
      const mappedProjects = fortnoxProjects.map(mapFortnoxProject)
      
      setProjects(mappedProjects)
    } catch (err) {
      // console.error('Error fetching projects:', err)
      setError(err instanceof Error ? err.message : 'Failed to load projects')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchProjects()
  }, [])

  const handleSaveProject = () => {
    setShowAddOverlay(false)
    setEditingProject(null)
    // Refresh the projects list after adding/updating a project
    fetchProjects()
  }

  const handleEditProject = (project: Project) => {
    setEditingProject(project)
  }

  const handleDeleteProject = (project: Project) => {
    setDeletingProject(project)
  }

  const confirmDeleteProject = async () => {
    if (!deletingProject) return

    setIsDeleting(true)
    
    try {
      const response = await apiFetch(`/fortnox/projects/${deletingProject.id}`, {
        method: 'DELETE',
      })

      if (!response.ok) {
        if (response.status === 400) {
          const errorData = await response.json()
          if (errorData.error === 'Fortnox not connected') {
            throw new Error('Fortnox är inte ansluten. Gå till Inställningar för att ansluta.')
          }
        }
        throw new Error(`Failed to delete project: ${response.statusText}`)
      }

      // Project deleted successfully
      setDeletingProject(null)
      fetchProjects() // Refresh the list
    } catch (err) {
      // console.error('Error deleting project:', err)
      setError(err instanceof Error ? err.message : 'Failed to delete project')
    } finally {
      setIsDeleting(false)
    }
  }

  const getStatusColor = (status: Project['status']) => {
    switch (status) {
      case 'active':
        return '#10b981'
      case 'completed':
        return '#6b7280'
      case 'on-hold':
        return '#f59e0b'
      default:
        return '#6b7280'
    }
  }

  const getStatusText = (status: Project['status']) => {
    switch (status) {
      case 'active':
        return 'Aktiv'
      case 'completed':
        return 'Slutförd'
      case 'on-hold':
        return 'Pausad'
      default:
        return 'Okänd'
    }
  }

  return (
    <>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e2e8f0',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{ 
              margin: '0 0 8px 0', 
              fontSize: '20px', 
              fontWeight: '600', 
              color: '#1a202c' 
            }}>
              Projekt
            </h2>
            <p style={{ 
              margin: '0', 
              color: '#64748b', 
              fontSize: '14px' 
            }}>
              Hantera och följ upp dina projekt
            </p>
          </div>
          <button
            onClick={() => setShowAddOverlay(true)}
            style={{
              padding: '12px 20px',
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#1d4ed8'
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#2563eb'
            }}
          >
            <span style={{ fontSize: '16px' }}>+</span>
            Nytt projekt
          </button>
        </div>

        {/* Project List */}
        <div style={{ padding: '0' }}>
          {loading ? (
            <div style={{
              padding: '60px 24px',
              textAlign: 'center',
              color: '#64748b'
            }}>
              <div style={{ fontSize: '24px', marginBottom: '16px' }}>⏳</div>
              <p style={{ margin: '0' }}>Laddar projekt från Fortnox...</p>
            </div>
          ) : error ? (
            <div style={{
              padding: '60px 24px',
              textAlign: 'center',
              color: '#ef4444'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
              <h3 style={{ margin: '0 0 8px 0', color: '#dc2626' }}>Kunde inte ladda projekt</h3>
              <p style={{ margin: '0 0 16px 0', color: '#64748b' }}>{error}</p>
              <button
                onClick={fetchProjects}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Försök igen
              </button>
            </div>
          ) : projects.length === 0 ? (
            <div style={{
              padding: '60px 24px',
              textAlign: 'center',
              color: '#64748b'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📁</div>
              <h3 style={{ margin: '0 0 8px 0', color: '#374151' }}>Inga projekt hittades</h3>
              <p style={{ margin: '0' }}>Det finns inga projekt i Fortnox för tillfället</p>
            </div>
          ) : (
            projects.map((project, index) => (
              <div
                key={project.id}
                style={{
                  padding: '20px 24px',
                  borderBottom: index < projects.length - 1 ? '1px solid #f1f5f9' : 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#f8fafc'
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '16px'
                }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{
                      margin: '0 0 8px 0',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#1a202c'
                    }}>
                      {project.name}
                    </h3>
                    {project.description && (
                      <p style={{
                        margin: '0 0 12px 0',
                        color: '#64748b',
                        fontSize: '14px',
                        lineHeight: '1.5'
                      }}>
                        {project.description}
                      </p>
                    )}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      fontSize: '12px',
                      color: '#64748b',
                      flexWrap: 'wrap'
                    }}>
                      <span>Projektnummer: {project.id}</span>
                      {project.startDate && <span>Startdatum: {new Date(project.startDate).toLocaleDateString('sv-SE')}</span>}
                      {project.projectLeader && <span>Projektledare: {project.projectLeader}</span>}
                      {project.contactPerson && <span>Kontaktperson: {project.contactPerson}</span>}
                      {project.endDate && <span>Slutdatum: {new Date(project.endDate).toLocaleDateString('sv-SE')}</span>}
                    </div>
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '500',
                      backgroundColor: `${getStatusColor(project.status)}20`,
                      color: getStatusColor(project.status)
                    }}>
                      {getStatusText(project.status)}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleEditProject(project)
                      }}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#f8fafc',
                        color: '#64748b',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = '#e2e8f0'
                        e.currentTarget.style.color = '#374151'
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = '#f8fafc'
                        e.currentTarget.style.color = '#64748b'
                      }}
                    >
                      Redigera
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDeleteProject(project)
                      }}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#fef2f2',
                        color: '#dc2626',
                        border: '1px solid #fecaca',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = '#fee2e2'
                        e.currentTarget.style.color = '#b91c1c'
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = '#fef2f2'
                        e.currentTarget.style.color = '#dc2626'
                      }}
                    >
                      Radera
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {showAddOverlay && (
        <ProjectOverlay
          title="Nytt projekt"
          onClose={() => setShowAddOverlay(false)}
          onSave={handleSaveProject}
        />
      )}

      {editingProject && (
        <ProjectOverlay
          title="Redigera projekt"
          project={editingProject}
          onClose={() => setEditingProject(null)}
          onSave={handleSaveProject}
        />
      )}

      {deletingProject && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            width: '100%',
            maxWidth: '400px',
            padding: '24px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: '#fef2f2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px'
              }}>
                ⚠️
              </div>
              <h3 style={{
                margin: '0',
                fontSize: '18px',
                fontWeight: '600',
                color: '#1a202c'
              }}>
                Radera projekt
              </h3>
            </div>
            
            <p style={{
              margin: '0 0 20px 0',
              color: '#64748b',
              fontSize: '14px',
              lineHeight: '1.5'
            }}>
              Är du säker på att du vill radera projektet <strong>"{deletingProject.name}"</strong>? 
              Denna åtgärd kan inte ångras och projektet kommer att tas bort från Fortnox.
            </p>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setDeletingProject(null)}
                disabled={isDeleting}
                style={{
                  padding: '10px 16px',
                  backgroundColor: 'transparent',
                  color: '#64748b',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s',
                  opacity: isDeleting ? 0.5 : 1
                }}
                onMouseOver={(e) => {
                  if (!isDeleting) {
                    e.currentTarget.style.backgroundColor = '#f8fafc'
                    e.currentTarget.style.borderColor = '#94a3b8'
                  }
                }}
                onMouseOut={(e) => {
                  if (!isDeleting) {
                    e.currentTarget.style.backgroundColor = 'transparent'
                    e.currentTarget.style.borderColor = '#d1d5db'
                  }
                }}
              >
                Avbryt
              </button>
              <button
                onClick={confirmDeleteProject}
                disabled={isDeleting}
                style={{
                  padding: '10px 16px',
                  backgroundColor: isDeleting ? '#9ca3af' : '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'background-color 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseOver={(e) => {
                  if (!isDeleting) {
                    e.currentTarget.style.backgroundColor = '#b91c1c'
                  }
                }}
                onMouseOut={(e) => {
                  if (!isDeleting) {
                    e.currentTarget.style.backgroundColor = '#dc2626'
                  }
                }}
              >
                {isDeleting && <span>⏳</span>}
                {isDeleting ? 'Raderar...' : 'Radera projekt'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
